import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronUpDownIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useSnackbar } from '../../providers/SnackbarContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import GraphqlService from '../../service/graphqlService';
import useAnalytics from '../../hooks/useAnalytics';
import { shouldShowIpWhitelist } from '../../utils/environmentConfig';
import usePermission from '../../authorization/permissionHook';
import { DELETE_ACCESS_KEYS, ENABLE_PHYSICAL_USER, GET_PHYSICAL_USER_TEMPLATES, ATTACH_TEMPLATE_TO_PHYSICAL_USER, DETACH_TEMPLATE_FROM_PHYSICAL_USER, SHOW_POLICY_TEMPLATES } from '../../utils/constants';
import { EXPIRATION_STATUS, getExpirationInfo, getExpirationPillStyle } from '../../utils/credentialExpiration';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

const AUDIT_ENTITY_CERTIFICATE_PROVISIONING = 'CertificateProvisioning';

// Same normalization as CreateCredentialModal.js - the platform's real service code is the
// enum-like "CERTIFICATE_PROVISION" (not the human phrase "certificate provisioning").
const normalizeTemplateText = (value) => (typeof value === 'string' ? value.toLowerCase().replace(/[^a-z]/g, '') : '');
const isCertificateProvisioningTemplate = (template) => {
  const servicesMatch = (template?.services || []).some((service) => normalizeTemplateText(service).includes('certificateprovision'));
  const descriptionMatch = normalizeTemplateText(template?.description).includes('certificateprovision');
  return servicesMatch || descriptionMatch;
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
    <p className="ml-2 text-gray-700">Loading history...</p>
  </div>
);

const CredentialDetailsModal = ({ isOpen, onClose, credential, onUpdate, canEdit = false, projects = [] }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    alertThreshold: credential?.quotaData?.threshold || '',
    quota: credential?.quotaData?.quota || '',
    iPWhiteList: credential?.logicalUserData?.iPWhiteList || '',
    pointOfContact: credential?.logicalUserData?.pointOfContact || '',
    description: credential?.description || '',
    id: credential?.id || '',
    // Feature 027 - project/brand codes this credential belongs to.
    brands: (credential?.logicalUserData?.projects || []).map((p) => p.code).filter((code) => code !== undefined && code !== null),
  });
  // Feature 027 - the last change request id(s) returned after a Brands update, so the
  // operator can track IAM propagation status without leaving the details view.
  const [lastChangeIds, setLastChangeIds] = useState([]);
  const [changeRequestStatuses, setChangeRequestStatuses] = useState({});
  const [isCheckingChangeStatus, setIsCheckingChangeStatus] = useState(false);


  // We compare directly with the credential prop when submitting

  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();

  // Fetch physical user details to get access keys status
  const {
    data: physicalUserData,
    isLoading: isLoadingPhysicalUser,
  } = useQuery(
    ['physicalUser', credential?.id],
    () => GraphqlService.getPhysicalUser(credential?.id),
    {
      enabled: isOpen && !!credential?.id,
      refetchOnWindowFocus: false,
    }
  );

  const { setAnalytics } = useAnalytics();

  // State for validation errors
  const [ipError, setIpError] = useState('');
  const [pointOfContactError, setPointOfContactError] = useState('');

  // State for updating spinner
  const [isUpdating, setIsUpdating] = useState(false)

  // FR020 #3 - Confirm rotation & remove old credentials: state for the
  // inline confirmation when removing a specific (old) access key.
  const [, canDeleteAccessKeys] = usePermission(DELETE_ACCESS_KEYS);
  const [, canEnablePhysicalUser] = usePermission(ENABLE_PHYSICAL_USER);
  const [, canViewPermissions] = usePermission(GET_PHYSICAL_USER_TEMPLATES);
  const [, canAttachTemplate] = usePermission(ATTACH_TEMPLATE_TO_PHYSICAL_USER);
  const [, canDetachTemplate] = usePermission(DETACH_TEMPLATE_FROM_PHYSICAL_USER);
  const [, canManagePolicyTemplates] = usePermission(SHOW_POLICY_TEMPLATES); // Feature 031 - cross-link hint
  const [keyPendingDeletion, setKeyPendingDeletion] = useState(null);
  const [isDeletingKey, setIsDeletingKey] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [templateIdToAttach, setTemplateIdToAttach] = useState('');
  const [isAttachingTemplate, setIsAttachingTemplate] = useState(false);
  const [templateIdPendingDetach, setTemplateIdPendingDetach] = useState(null);
  const [isDetachingTemplate, setIsDetachingTemplate] = useState(false);

  // Feature 026 - catalog of all available API templates + the ones currently attached
  const {
    data: allTemplates,
    isLoading: isLoadingAllTemplates,
    isError: isErrorAllTemplates,
  } = useQuery(
    ['permissionTemplates'],
    () => GraphqlService.getTemplates(),
    { enabled: isOpen && canViewPermissions, staleTime: 5 * 60 * 1000 }
  );
  const {
    data: attachedTemplateIds,
    isLoading: isLoadingAttachedTemplates,
    isError: isErrorAttachedTemplates,
  } = useQuery(
    ['physicalUserTemplates', credential?.id],
    () => GraphqlService.getPhysicalUserTemplates(credential?.id),
    { enabled: isOpen && !!credential?.id && canViewPermissions, refetchOnWindowFocus: false }
  );
  // Both queries feed the same section - wait for both before rendering anything,
  // otherwise the list would render empty first and then "jump" once the slower
  // of the two resolves.
  const isLoadingTemplates = isLoadingAllTemplates || isLoadingAttachedTemplates;
  const isErrorTemplates = isErrorAllTemplates || isErrorAttachedTemplates;

  const attachedTemplates = (allTemplates || []).filter((template) => (attachedTemplateIds || []).includes(template.templateId));
  const hasCertificateProvisioningAttached = attachedTemplates.some(isCertificateProvisioningTemplate);
  const availableTemplatesToAttach = (allTemplates || []).filter((template) => {
    if ((attachedTemplateIds || []).includes(template.templateId)) return false;
    if (hasCertificateProvisioningAttached) return false; // exclusive - nothing else can be added
    if (isCertificateProvisioningTemplate(template) && attachedTemplates.length > 0) return false; // can't add cert if other APIs already attached
    return true;
  });

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      await GraphqlService.enablePhysicalUser(credential.id);
      openSnackBar('Credential enabled successfully!', 'success');
      queryClient.invalidateQueries(['physicalUser', credential.id]);
      queryClient.invalidateQueries(['physicalUsers']);
      queryClient.invalidateQueries(['auditLogs', credential.id]);
    } catch (error) {
      openSnackBar(`Failed to enable credential: ${error.message}`, 'error');
    } finally {
      setIsEnabling(false);
    }
  };

  const handleAttachTemplate = async () => {
    if (!templateIdToAttach) return;
    setIsAttachingTemplate(true);
    try {
      await GraphqlService.attachTemplateToPhysicalUser(credential.id, templateIdToAttach);
      openSnackBar('API added successfully!', 'success');
      setTemplateIdToAttach('');
      queryClient.invalidateQueries(['physicalUserTemplates', credential.id]);
      queryClient.invalidateQueries(['auditLogs', credential.id]);
    } catch (error) {
      openSnackBar(`Failed to add API: ${error.message}`, 'error');
    } finally {
      setIsAttachingTemplate(false);
    }
  };

  const handleConfirmDetachTemplate = async (templateId) => {
    setIsDetachingTemplate(true);
    try {
      await GraphqlService.detachTemplateFromPhysicalUser(credential.id, templateId);
      openSnackBar('API removed successfully!', 'success');
      queryClient.invalidateQueries(['physicalUserTemplates', credential.id]);
      queryClient.invalidateQueries(['auditLogs', credential.id]);
    } catch (error) {
      openSnackBar(`Failed to remove API: ${error.message}`, 'error');
    } finally {
      setIsDetachingTemplate(false);
      setTemplateIdPendingDetach(null);
    }
  };

  const isRecentlyUsed = (lastUsedDate) => {
    if (!lastUsedDate) return false;
    const lastUsed = new Date(lastUsedDate).getTime();
    if (Number.isNaN(lastUsed)) return false;
    return Date.now() - lastUsed < 24 * 60 * 60 * 1000; // used within the last 24h
  };

  const handleRequestDeleteKey = (accessKeyId) => {
    setKeyPendingDeletion(accessKeyId);
  };

  const handleCancelDeleteKey = () => {
    setKeyPendingDeletion(null);
  };

  const handleConfirmDeleteKey = async (accessKeyId) => {
    setIsDeletingKey(true);
    try {
      await GraphqlService.deleteAccessKeys(credential.id, accessKeyId);
      openSnackBar('Old access key removed successfully!', 'success');
      queryClient.invalidateQueries(['physicalUser', credential.id]);
      queryClient.invalidateQueries(['physicalUsers']);
      queryClient.invalidateQueries(['auditLogs', credential.id]);
    } catch (error) {
      console.error('Error deleting access key:', error);
      openSnackBar(`Failed to remove access key: ${error.message}`, 'error');
    } finally {
      setIsDeletingKey(false);
      setKeyPendingDeletion(null);
    }
  };

  // Audit Log State
  const [auditPage, setAuditPage] = useState(1);
  const auditItemsPerPage = 5;

  const {
    data: auditLogData,
    isLoading: isLoadingAuditLog,
    isError: isErrorAuditLog,
    refetch: refetchAuditLogs,
  } = useQuery(
    ['auditLogs', credential?.id, auditPage],
    () => GraphqlService.getAuditLogs({
      entity: AUDIT_ENTITY_CERTIFICATE_PROVISIONING,
      entity_id: credential?.id,
      page: auditPage,
      limit: auditItemsPerPage,
    }),
    {
      enabled: activeTab === 'history' && !!credential?.id,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (activeTab === 'history') {
      refetchAuditLogs();
    }
  }, [activeTab, auditPage, refetchAuditLogs]);

  if (!isOpen || !credential) return null;

  const handleCancel = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-info-cancel' }
    });
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'iPWhiteList') {
      if (value.trim() === '') {
        setIpError(''); // Clear error if field is empty (assuming it's required, submit will catch)
        return;
      }
      const ips = value.split(',').map(ip => ip.trim());
      const ipRegex = /^([0-9]{1,3}\.){3}[0-9]{1,3}$/;
      const invalidIPs = ips.filter(ip => ip !== '' && !ipRegex.test(ip));
      if (invalidIPs.length > 0) {
        setIpError(`Invalid IP addresses: ${invalidIPs.join(', ')}. Please use comma-separated valid IPs.`);
      } else {
        setIpError('');
      }
    } else if (name === 'pointOfContact') {
      if (value.trim() === '') {
        setPointOfContactError(''); // Clear error if field is empty
        return;
      }
      const emails = value.split(',').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => email !== '' && !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        setPointOfContactError(`Invalid email addresses: ${invalidEmails.join(', ')}. Please use comma-separated valid emails.`);
      } else {
        setPointOfContactError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-info-update' }
    });

    if (ipError) {
      openSnackBar('Please correct the whitelisted IP addresses.', 'error');
      return;
    }
    if (pointOfContactError) {
      openSnackBar('Please correct the points of contact email addresses.', 'error');
      return;
    }

    // iPWhiteList is only required in prod
    if (shouldShowIpWhitelist() && !formData.iPWhiteList) {
      openSnackBar('Please fill in all required fields.', 'error');
      return;
    }
    const hasQuota = Boolean(credential.quotaData);
    if ((hasQuota && !formData.alertThreshold) || !formData.pointOfContact) {
      openSnackBar('Please fill in all required fields.', 'error');
      return;
    }

    setIsUpdating(true);

    // Track which fields actually changed
    const changedFields = {};

    // Compare current values with original credential values
    if (formData.description !== credential.description) {
      changedFields.description = formData.description;
    }

    if (hasQuota && String(formData.alertThreshold) !== String(credential.quotaData?.threshold)) {
      changedFields.alertThreshold = formData.alertThreshold;
    }

    // Handle IP whitelist comparison - normalize both to sorted arrays of trimmed values
    const normalizeIPList = (ipList) => {
      if (!ipList) return [];
      if (Array.isArray(ipList)) {
        return ipList.map(ip => ip.trim()).filter(Boolean).sort();
      }
      return ipList.split(',').map(ip => ip.trim()).filter(Boolean).sort();
    };

    const currentIPList = normalizeIPList(formData.iPWhiteList);
    const originalIPList = normalizeIPList(credential.logicalUserData?.iPWhiteList);

    // Deep compare the arrays
    const ipListsEqual =
      currentIPList.length === originalIPList.length &&
      currentIPList.every((ip, i) => ip === originalIPList[i]);

    if (!ipListsEqual) {
      changedFields.iPWhiteList = formData.iPWhiteList;
    }

    // Handle points of contact comparison - normalize both to sorted arrays of trimmed values
    const normalizeContactList = (contacts) => {
      if (!contacts) return [];
      if (Array.isArray(contacts)) {
        return contacts.map(contact => contact.trim()).filter(Boolean).sort();
      }
      return contacts.split(',').map(contact => contact.trim()).filter(Boolean).sort();
    };

    const currentContactList = normalizeContactList(formData.pointOfContact);
    const originalContactList = normalizeContactList(credential.logicalUserData?.pointOfContact);

    // Deep compare the arrays
    const contactListsEqual =
      currentContactList.length === originalContactList.length &&
      currentContactList.every((contact, i) => contact === originalContactList[i]);

    if (!contactListsEqual) {
      changedFields.pointOfContact = formData.pointOfContact;
    }

    // Feature 027 - project/brand list comparison (sorted, numeric codes).
    const normalizeBrandList = (codes) => [...(codes || [])].map(Number).sort((a, b) => a - b);
    const currentBrandList = normalizeBrandList(formData.brands);
    const originalBrandList = normalizeBrandList((credential.logicalUserData?.projects || []).map((p) => p.code));
    const brandListsEqual =
      currentBrandList.length === originalBrandList.length &&
      currentBrandList.every((code, i) => code === originalBrandList[i]);

    if (!brandListsEqual) {
      if (currentBrandList.length === 0) {
        openSnackBar('A credential must belong to at least one product.', 'error');
        setIsUpdating(false);
        return;
      }
      changedFields.brands = currentBrandList;
    }

    try {
      const { changeIds } = await GraphqlService.updateLogicalUser(
        credential.logicalUser,
        credential.userId,
        credential.id,
        Array.isArray(formData.iPWhiteList) ? formData.iPWhiteList : formData.iPWhiteList.split(',').map(ip => ip.trim()),
        formData.pointOfContact?.trim(),
        formData.description || '',
        hasQuota && formData.alertThreshold ? parseInt(formData.alertThreshold, 10) : null,
        changedFields.brands
      );

      // Check if any fields were actually changed
      if (Object.keys(changedFields).length === 0) {
        openSnackBar('No changes detected.', 'info');
      } else {
        openSnackBar('Credential updated successfully!', 'success');

        // Pass only the changed fields along with the ID to the parent component
        onUpdate(credential.id, {
          ...changedFields,
          // Always include the ID and original credential for reference
          id: credential.id,
          originalCredential: credential
        });
      }

      // Always refresh data
      queryClient.invalidateQueries(['physicalUsers']);

      // Feature 027 - a Brands change returns change request id(s) for IAM propagation
      // tracking; keep the modal open so the operator can see/check them instead of
      // closing immediately.
      if (changeIds && changeIds.length > 0) {
        setLastChangeIds(changeIds);
      } else {
        onClose();
      }
    } catch (error) {
      console.log('Error updating credential:', error);
      openSnackBar('Failed to update credential. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Feature 027 - check IAM propagation status of a Brands change request.
  const handleCheckChangeStatus = async (changeId) => {
    setIsCheckingChangeStatus(true);
    try {
      const status = await GraphqlService.getChangeRequestStatus(changeId);
      setChangeRequestStatuses((prev) => ({ ...prev, [changeId]: status }));
    } catch (error) {
      openSnackBar('Failed to check change request status.', 'error');
    } finally {
      setIsCheckingChangeStatus(false);
    }
  };


  const auditLogs = auditLogData?.data || [];
  const auditLogPagination = auditLogData?.pagination || {};

  const modalWidthClass = activeTab === 'info' ? 'max-w-lg' : 'max-w-3xl';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className={`bg-white rounded-lg shadow-xl w-full ${modalWidthClass} max-h-[90vh] flex flex-col`}>
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">{credential.name}</h3>
            {!canEdit && (
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-md">Read Only</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
          <div className="p-6">
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${activeTab === 'info'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  Info
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  Change History
                </button>
              </nav>
            </div>

            {activeTab === 'info' && (
              <form onSubmit={handleSubmit} id="credentialDetailsForm" className="space-y-4">
                <div>
                  <label htmlFor="credentialId" className="block text-sm font-medium text-gray-700 mb-1">Credential ID</label>
                  <input
                    type="text"
                    name="credentialId"
                    id="credentialId"
                    value={credential.id || 'N/A'}
                    disabled
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">This is the unique identifier for this credential and cannot be edited.</p>
                </div>

                <div>
                  <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                  <input
                    type="text"
                    name="userId"
                    id="userId"
                    value={credential.userId || 'N/A'}
                    disabled
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">This is the unique identifier for this user and cannot be edited.</p>
                </div>

                <div data-tutorial="detail-brands">
                  <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                  {canEdit && projects.length > 0 ? (
                    <MultiSelectDropdown
                      options={projects}
                      selectedValues={formData.brands}
                      onChange={(brands) => setFormData((prev) => ({ ...prev, brands }))}
                      placeholder="Select Products"
                    />
                  ) : (
                    <div className="p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm min-h-[40px]">
                      {credential.logicalUserData?.projects && credential.logicalUserData?.projects.length > 0 ? (
                        credential.logicalUserData.projects
                          .filter(project => project.name || project.code)
                          .map(project => (
                            <span key={project.code || project.name} className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs font-semibold mr-2 mb-2">
                              {project.name || project.code}
                            </span>
                          ))
                      ) : (
                        <span className="text-gray-500">No Products</span>
                      )}
                    </div>
                  )}
                  {canEdit && (
                    <p className="mt-1 text-xs text-gray-500">Changing this triggers an IAM policy update on the platform (may take a moment to propagate).</p>
                  )}
                  {lastChangeIds.length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 space-y-1">
                      {lastChangeIds.map((changeId) => (
                        <div key={changeId} className="flex items-center justify-between gap-2">
                          <span>
                            Change request <span className="font-mono">{changeId}</span>
                            {changeRequestStatuses[changeId] && `: ${changeRequestStatuses[changeId].status}`}
                          </span>
                          <button
                            type="button"
                            disabled={isCheckingChangeStatus}
                            onClick={() => handleCheckChangeStatus(changeId)}
                            className="px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded disabled:opacity-50 shrink-0"
                          >
                            {isCheckingChangeStatus ? 'Checking…' : 'Check status'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter a description for this credential"
                    disabled={!canEdit}
                  ></textarea>
                </div>

                {/* Feature 030 - Expiration status, derived from ExpirationDate + LatestRotation */}
                <div data-tutorial="detail-expiration">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                  <div className="p-3 border border-gray-300 rounded-md shadow-sm bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                    {(() => {
                      const { status, daysUntilExpiry } = getExpirationInfo(credential.expirationDate);
                      return (
                        <span className={getExpirationPillStyle(status)}>
                          {status}
                          {status === EXPIRATION_STATUS.EXPIRING_SOON && daysUntilExpiry !== null && ` (${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'})`}
                        </span>
                      );
                    })()}
                    <span className="text-xs text-gray-500">
                      {credential.expirationDate ? `Expires: ${new Date(credential.expirationDate).toLocaleDateString()}` : 'No expiration date set'}
                      {credential.latestRotation && ` \u2022 Last rotated: ${new Date(credential.latestRotation).toLocaleDateString()}`}
                    </span>
                  </div>
                </div>


                {credential.quotaData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="quota" className="block text-sm font-medium text-gray-700 mb-1">Quota</label>
                    <div className="relative">
                      <input
                        type="number"
                        name="quota"
                        id="quota"
                        value={String(formData.quota || '0')}
                        disabled
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm pr-10 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">This field cannot be edited directly. Use the &quot;Increase Quota&quot; action from the main screen.</p>
                  </div>
                  <div>
                    <label htmlFor="alertThreshold" className="block text-sm font-medium text-gray-700 mb-1">Alert Threshold <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="number"
                        name="alertThreshold"
                        id="alertThreshold"
                        min="0"
                        value={String(formData.alertThreshold || '0')}
                        onChange={handleInputChange}
                        required
                        disabled={!canEdit}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="Alert when quota reaches this"
                      />
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">When the quota reaches this number, alerts will be shown. Set this to prepare for quota depletion.</p>
                  </div>
                </div>
                )}

                {shouldShowIpWhitelist() && (
                  <div>
                    <label htmlFor="iPWhiteList" className="block text-sm font-medium text-gray-700 mb-1">Whitelisted IPs <span className="text-red-500">*</span></label>
                    <textarea
                      name="iPWhiteList"
                      id="iPWhiteList"
                      rows="3"
                      value={formData.iPWhiteList}
                      onChange={handleInputChange}
                      required
                      disabled={!canEdit}
                      className={`w-full p-2 border ${ipError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="e.g., 10.42.145.12, 192.168.1.0/24"
                    ></textarea>
                    {ipError && <p className="text-red-500 text-xs mt-1">{ipError}</p>}
                    <p className="mt-1 text-xs text-gray-500">Enter a comma-separated list of IP addresses that are allowed to use this credential. Each IP must be in standard format (e.g., 192.168.1.1).</p>
                  </div>
                )}
                <div>
                  <label htmlFor="pointOfContact" className="block text-sm font-medium text-gray-700 mb-1">Points of contact <span className="text-red-500">*</span></label>
                  <textarea
                    name="pointOfContact"
                    id="pointOfContact"
                    rows="3"
                    value={formData.pointOfContact}
                    onChange={handleInputChange}
                    required
                    disabled={!canEdit}
                    className={`w-full p-2 border ${pointOfContactError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="e.g., email1@mail.com, email2@mail.com"
                  ></textarea>
                  {pointOfContactError && <p className="text-red-500 text-xs mt-1">{pointOfContactError}</p>}
                  <p className="mt-1 text-xs text-gray-500">Enter a comma-separated list of email addresses to contact regarding this credential. These contacts will receive notifications about quota alerts.</p>
                </div>

                {/* Feature 026 - Enable banner, shown when the credential has no active access key */}
                {!isLoadingPhysicalUser && physicalUserData?.accessKeys && physicalUserData.accessKeys.length > 0
                  && !physicalUserData.accessKeys.some((key) => key.status === 'Active') && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-md flex items-center justify-between" data-tutorial="detail-enable">
                    <p className="text-sm text-amber-800">This credential is disabled (no active access key).</p>
                    {canEnablePhysicalUser && (
                      <button
                        type="button"
                        disabled={isEnabling}
                        onClick={handleEnable}
                        className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
                      >
                        {isEnabling ? 'Enabling…' : 'Enable'}
                      </button>
                    )}
                  </div>
                )}

                {/* Access Keys Status Section */}
                <div data-tutorial="detail-access-keys">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Access Keys</label>
                  <div className="p-3 border border-gray-300 rounded-md shadow-sm bg-gray-50">
                    {isLoadingPhysicalUser ? (
                      <div className="flex items-center text-gray-500 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2" />
                        Loading access keys...
                      </div>
                    ) : physicalUserData?.accessKeys && physicalUserData.accessKeys.length > 0 ? (
                      <div className="space-y-2">
                        {physicalUserData.accessKeys.map((key, index) => (
                          <div key={key.accessKeyId || index} className="bg-white p-2 rounded border border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-mono text-gray-700">
                                {key.accessKeyId ? `${key.accessKeyId.substring(0, 8)}...${key.accessKeyId.substring(key.accessKeyId.length - 4)}` : 'Unknown Key'}
                              </span>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  key.status === 'Active' 
                                    ? 'bg-green-100 text-green-700 border border-green-400' 
                                    : 'bg-red-100 text-red-700 border border-red-400'
                                }`}>
                                  {key.status || 'Unknown'}
                                </span>
                                {canDeleteAccessKeys && physicalUserData.accessKeys.length > 1 && key.accessKeyId && (
                                  <button
                                    type="button"
                                    onClick={() => handleRequestDeleteKey(key.accessKeyId)}
                                    title="Remove this access key"
                                    className="text-gray-400 hover:text-red-600"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            {key.lastUsedDate && (
                              <p className="text-xs text-gray-500 mt-1">Last used: {new Date(key.lastUsedDate).toLocaleString()}</p>
                            )}
                            {keyPendingDeletion === key.accessKeyId && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-300 rounded">
                                <p className="text-xs text-yellow-800 mb-2">
                                  Remove this access key? This cannot be undone.
                                  {isRecentlyUsed(key.lastUsedDate) && ' Warning: this key was used within the last 24 hours.'}
                                </p>
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    disabled={isDeletingKey}
                                    onClick={() => handleConfirmDeleteKey(key.accessKeyId)}
                                    className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                                  >
                                    {isDeletingKey ? 'Removing…' : 'Yes, remove'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isDeletingKey}
                                    onClick={handleCancelDeleteKey}
                                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">No access keys found</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Shows the status of access keys associated with this credential. Active keys can authenticate; Inactive keys are disabled. When two keys exist (e.g. after a rotation), you can remove the old one once you have confirmed the new one works.</p>
                </div>

                {/* Feature 026 - Permissions (API templates) Section */}
                {canViewPermissions && (
                  <div data-tutorial="detail-permissions">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Permissions</label>
                    <div className="p-3 border border-gray-300 rounded-md shadow-sm bg-gray-50">
                      {isLoadingTemplates ? (
                        <div className="flex items-center text-gray-500 text-sm">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2" />
                          Loading permissions...
                        </div>
                      ) : isErrorTemplates ? (
                        <span className="text-red-500 text-sm">Error loading permissions.</span>
                      ) : attachedTemplates.length > 0 ? (
                        <div className="space-y-2">
                          {attachedTemplates.map((template) => (
                            <div key={template.templateId} className="bg-white p-2 rounded border border-gray-200">
                              <div className="flex items-center justify-between">
                                <span>
                                  <span className="text-sm text-gray-800">{template.description || template.templateId}</span>
                                  {template.services && template.services.length > 0 && (
                                    <span className="block text-xs text-gray-500">{template.services.join(', ')}</span>
                                  )}
                                </span>
                                {canDetachTemplate && (
                                  <button
                                    type="button"
                                    onClick={() => setTemplateIdPendingDetach(template.templateId)}
                                    title="Remove this API"
                                    className="text-gray-400 hover:text-red-600 ml-2 shrink-0"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                              {templateIdPendingDetach === template.templateId && (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-300 rounded">
                                  <p className="text-xs text-yellow-800 mb-2">
                                    Remove this API? This cannot be undone.
                                    {attachedTemplates.length === 1 && ' Warning: this is the last permission on this credential.'}
                                  </p>
                                  <div className="flex space-x-2">
                                    <button
                                      type="button"
                                      disabled={isDetachingTemplate}
                                      onClick={() => handleConfirmDetachTemplate(template.templateId)}
                                      className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                                    >
                                      {isDetachingTemplate ? 'Removing…' : 'Yes, remove'}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isDetachingTemplate}
                                      onClick={() => setTemplateIdPendingDetach(null)}
                                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No permissions found</span>
                      )}
                      {!isLoadingTemplates && !isErrorTemplates && canAttachTemplate && availableTemplatesToAttach.length > 0 && (
                        <div className="mt-3 flex items-center space-x-2 w-full">
                          <select
                            value={templateIdToAttach}
                            onChange={(e) => setTemplateIdToAttach(e.target.value)}
                            className="flex-1 min-w-0 w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500 truncate"
                          >
                            <option value="">Select an API to add…</option>
                            {availableTemplatesToAttach.map((template) => (
                              <option key={template.templateId} value={template.templateId}>
                                {template.description || template.templateId}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            disabled={!templateIdToAttach || isAttachingTemplate}
                            onClick={handleAttachTemplate}
                            className="text-xs px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 shrink-0"
                          >
                            {isAttachingTemplate ? 'Adding…' : 'Add'}
                          </button>
                        </div>
                      )}
                      {!isLoadingTemplates && !isErrorTemplates && hasCertificateProvisioningAttached && (
                        <p className="text-xs text-amber-700 mt-2">Certificate Provisioning cannot be combined with other APIs.</p>
                      )}
                      {canManagePolicyTemplates && (
                        <p className="text-xs text-gray-400 mt-2">
                          Don&apos;t see the API you need? <a href="#/policyTemplates" className="text-indigo-600 hover:text-indigo-800">Manage Policy Templates</a>.
                        </p>
                      )}
                    </div>
                  </div>
                )}

              </form>
            )}

            {activeTab === 'history' && (
              isLoadingAuditLog ? <LoadingSpinner /> :
                isErrorAuditLog ? <p className="text-red-500 text-center">Error loading change history.</p> :
                  auditLogs.length === 0 ? <p className="text-gray-600 text-center p-4">No change history found for this credential.</p> :
                    (
                      <div className="space-y-4">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detail</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Value</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {auditLogs.map((log) => (
                              <tr key={log.id}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.change}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.detail || '-'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.value || '-'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.user_name}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{new Date(log.action_timestamp).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {auditLogPagination.totalPages > 1 && (
                          <div className="p-4 flex justify-end items-center text-sm text-gray-600">
                            <span>
                              Page {auditLogPagination.pageNumber} of {auditLogPagination.totalPages}
                            </span>
                            <button
                              type="button"
                              onClick={() => setAuditPage(prev => Math.max(prev - 1, 1))}
                              disabled={auditPage === 1}
                              className="p-1 ml-4 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                            >
                              <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setAuditPage(prev => Math.min(prev + 1, auditLogPagination.totalPages))}
                              disabled={auditPage === auditLogPagination.totalPages || auditLogPagination.totalPages === 0}
                              className="p-1 ml-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                            >
                              <ChevronRightIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUpdating}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          {activeTab === 'info' && canEdit && (
            <button
              type="submit"
              form="credentialDetailsForm"
              disabled={isUpdating}
              className="ml-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Update
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialDetailsModal;
